// filesV6.tsx — shared visual primitives for the Files & Attachments V6 redesign.
// Values from docs/design/design_handoff_files_v6 (README "Design Tokens" +
// assets/intelligenic-tokens.css). The app does not define those CSS variables,
// so the concrete values live here (same precedent as IngestionCard.tsx).
import React from "react";
import { File as FileIcon, Globe, Network } from "lucide-react";
import type { FileAssignments } from "@/api/fileAssignments";

export const T = {
    primary: "#1954ED",
    primaryHover: "#103699",
    spineDeep: "#103699",
    spineSoft: "#EDF2FF",
    spineBorder: "#A3BBF8",
    danger: "#dc2626",
    bgPaper: "#ffffff",
    bgPanel: "#f8fafc",
    bgInput: "#f3f3f5",
    fg1: "#111827",
    fg2: "#374151",
    fg3: "#4b5563",
    fg4: "#6b7280",
    divider: "rgba(0,0,0,0.07)",
    mono: "ui-monospace, 'SF Mono', Menlo, monospace"
} as const;

// The design ships per-product colors; the Project model has none, so we map
// project_id into a fixed palette — deterministic across renders and screens.
export const PRODUCT_PALETTE = [
    "#F7F9FF", "#ECF1FF", "#94B2FF", "#1954ED",
    "#DBE5FF", "#103699", "#EDF2FF", "#A3BBF8"
] as const;

export function productColor(projectId: number): string {
    return PRODUCT_PALETTE[Math.abs(Math.trunc(projectId)) % PRODUCT_PALETTE.length];
}

export type FileKind = "document" | "image" | "video" | "audio" | "archive" | "code" | "other";

export function fileKindFromName(filename?: string): FileKind {
    const e = (filename?.split(".").pop() || "").toLowerCase();
    const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
    const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
    const audioExts = ["mp3", "wav", "flac", "aac", "ogg", "wma"];
    const archiveExts = ["zip", "rar", "7z", "tar", "gz", "bz2"];
    const codeExts = ["js", "ts", "jsx", "tsx", "py", "java", "cpp", "c", "php", "rb", "go", "rs", "sql", "html", "css", "json", "md"];
    const docExts = ["pdf", "doc", "docx", "txt", "rtf", "odt", "xls", "xlsx", "ppt", "pptx"];

    if (imageExts.includes(e)) return "image";
    if (videoExts.includes(e)) return "video";
    if (audioExts.includes(e)) return "audio";
    if (archiveExts.includes(e)) return "archive";
    if (codeExts.includes(e)) return "code";
    if (docExts.includes(e)) return "document";
    return "other";
}

// The meta line's "Kind" — VectorFile has no kind field, so the extension-derived
// type stands in (spec §2 "Files list").
export function fileKindLabel(kind: FileKind): string {
    if (kind === "other") return "File";
    return kind.charAt(0).toUpperCase() + kind.slice(1);
}

export function fileKindColor(kind: FileKind): string {
    switch (kind) {
        case "document": return "#1954ED";
        case "image": return "#008000";
        case "video": return "#f57c00";
        case "audio": return "#7b1fa2";
        case "archive": return "#5d4037";
        case "code": return "#d32f2f";
        default: return "#808080";
    }
}

export function formatFileSize(bytes: number): string {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// 34px tinted square with the extension badge bottom-right (handoff FileTypeTile).
export function FileTypeTile({ filename, size = 34 }: { filename?: string; size?: number }) {
    const kind = fileKindFromName(filename);
    const color = fileKindColor(kind);
    const ext = (filename?.includes(".") ? filename.split(".").pop() || "" : "").toUpperCase().slice(0, 5);
    return (
        <span
            style={{
                width: size, height: size, borderRadius: 9, flex: "0 0 auto", position: "relative",
                background: `${color}15`, border: `1px solid ${color}33`,
                display: "inline-flex", alignItems: "center", justifyContent: "center"
            }}
        >
            <FileIcon size={Math.round(size * 0.46)} color={color} strokeWidth={1.6} />
            {ext && (
                <span
                    style={{
                        position: "absolute", bottom: -5, right: -4, fontSize: 7.5, fontWeight: 800,
                        letterSpacing: "0.03em", color: "#fff", background: color, padding: "1px 4px", borderRadius: 4
                    }}
                >
                    {ext}
                </span>
            )}
        </span>
    );
}

// Round color dot — the product glyph (README: "product glyph dot (12px, product color)").
export function ProductGlyph({ color, size = 12 }: { color: string; size?: number }) {
    return <span style={{ width: size, height: size, borderRadius: 999, background: color, flex: "0 0 auto", display: "inline-block" }} />;
}

// Matches the POST /api/vector_files/get_file_scopes response entries.
export interface FileScopeShape {
    global: { company_id: number; company_name: string } | null;
    projects: Array<{ project_id: number; project_name: string }>;
    features: Array<{ feature_id: number; feature_name: string }>;
}

// Empty scope is implicitly global today (backend treats no-scope as company-wide),
// so the list shows "All products" for both. Feature-only legacy scopes are NOT
// global — they render no chips and resolve the next time Assign is saved.
export function isEffectivelyGlobal(scope: FileScopeShape | null | undefined): boolean {
    if (!scope) return true;
    if (scope.global) return true;
    return scope.projects.length === 0 && scope.features.length === 0;
}

// Every chip is a fixed 250px so pills line up column-like across rows; names
// that don't fit ellipsize, with the full name available on hover via title.
const chipBase: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 11px 4px 7px", borderRadius: 999, whiteSpace: "nowrap",
    width: 250, boxSizing: "border-box", flex: "0 0 auto", overflow: "hidden"
};

const chipLabelBase: React.CSSProperties = {
    fontSize: 11.5, fontWeight: 600, minWidth: 0,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
};

export function AllProductsChip() {
    return (
        <span title="All products" style={{ ...chipBase, background: T.spineSoft, border: `1px solid ${T.spineBorder}` }}>
            <Globe size={12} color={T.spineDeep} style={{ flexShrink: 0 }} />
            <span style={{ ...chipLabelBase, color: T.spineDeep }}>All products</span>
        </span>
    );
}

export function ProductChip({ projectId, name }: { projectId: number; name: string }) {
    return (
        <span title={name} style={{ ...chipBase, background: T.bgPanel, border: `1px solid ${T.divider}` }}>
            <ProductGlyph color={productColor(projectId)} size={12} />
            <span style={{ ...chipLabelBase, color: T.fg2 }}>{name}</span>
        </span>
    );
}

export function ContextMeshChip({ name }: { name: string }) {
    return (
        <span title={`Context Mesh: ${name}`} style={{ ...chipBase, background: "#DBE5FF", border: `1px solid ${T.spineBorder}` }}>
            <Network size={12} color={T.spineDeep} style={{ flexShrink: 0 }} />
            <span style={{ ...chipLabelBase, color: T.spineDeep }}>Context Mesh: {name}</span>
        </span>
    );
}

function CountChip({ label, mesh = false }: { label: string; mesh?: boolean }) {
    return (
        <span
            title={label}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 9px",
                borderRadius: 999,
                flex: "0 0 auto",
                fontSize: 11.5,
                fontWeight: 700,
                color: mesh ? T.spineDeep : T.fg2,
                background: mesh ? "#DBE5FF" : T.bgPanel,
                border: `1px solid ${mesh ? T.spineBorder : T.divider}`
            }}
        >
            {label}
        </span>
    );
}

export function AssignmentChips({ assignment }: { assignment: FileAssignments | null | undefined }) {
    if (!assignment) return null;
    const productItems = assignment.products.items;
    const meshItems = assignment.contextMeshes.items;
    const productCount = assignment.products.ids.length;
    const meshCount = assignment.contextMeshes.ids.length;

    if (assignment.products.mode === "selected" && productCount === 0 && meshCount === 0) {
        return <CountChip label="Unassigned" />;
    }

    return (
        <>
            {assignment.products.mode === "all" && <AllProductsChip />}
            {assignment.products.mode === "selected" && productItems[0] && (
                <ProductChip projectId={productItems[0].id} name={`Product: ${productItems[0].name}`} />
            )}
            {assignment.products.mode === "selected" && productCount > 1 && (
                <CountChip label={`+${productCount - 1} Product${productCount === 2 ? "" : "s"}`} />
            )}
            {meshItems[0] && <ContextMeshChip name={meshItems[0].name} />}
            {meshCount > 1 && <CountChip mesh label={`+${meshCount - 1} Context Mesh${meshCount === 2 ? "" : "es"}`} />}
        </>
    );
}

export function ScopeChips({ scope }: { scope: FileScopeShape | null | undefined }) {
    if (isEffectivelyGlobal(scope)) return <AllProductsChip />;
    return (
        <>
            {scope!.projects.map(p => (
                <ProductChip key={p.project_id} projectId={p.project_id} name={p.project_name} />
            ))}
        </>
    );
}
